import { captureException } from '@sentry/minimal';
import { useEffect } from 'react';
import { ProgramFragment } from '../documents/getProgram.generated';
import { useGetProgramsQuery } from '../documents/getProgramsQuery.generated';
import { ProgramCategory } from '../models/program';

export function usePrograms(): {
    programs: readonly ProgramFragment[] | null;
    reload: () => void;
} {
    const result = useGetProgramsQuery({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: false,
    });
    const { error, data, loading, refetch } = result;

    useEffect(() => {
        captureException(error);
    }, [error]);

    if (loading) {
        return {
            programs: null,
            reload: refetch,
        };
    }

    if (error || !data || !data.programs) {
        return {
            programs: [],
            reload: refetch,
        };
    }

    return {
        programs: [
            ...data.programs.filter((program): program is ProgramFragment => program != null),
        ],
        reload: refetch,
    };
}

export function useProgramsForPreview(): readonly ProgramFragment[] {
    return usePrograms().programs?.slice(0, 5) ?? [];
}

export function useCategories(): {
    categories: readonly ProgramCategory[] | null;
    reload: () => void;
} {
    const { programs, reload } = usePrograms();

    if (!programs) {
        return {
            categories: null,
            reload,
        };
    }

    return {
        reload,
        categories: [
            ...Object.values(
                programs.reduce((prev, next) => {
                    if (!next || !next.category) {
                        return prev;
                    }

                    if (!prev[next.category]) {
                        prev[next.category] = { programs: [], name: next.category };
                    }

                    prev[next.category].programs.push(next);

                    return prev;
                }, {} as Record<string, ProgramCategory>),
            ),
        ],
    };
}
